import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getPascoService } from '@src/container';
import type { PascoDTO } from '@src/application/pascos/dto';

/**
 * Hook: useApprovePasco
 * Approves a PASCO record for processing
 * 
 * @param options - React Query mutation options
 * @returns Mutation result for approving PASCO
 * 
 * @example
 * ```typescript
 * const { mutate: approvePasco, isPending, error } = useApprovePasco({
 *     onSuccess: (approvedPasco) => {
 *         console.log('PASCO approved:', approvedPasco);
 *     }
 * });
 * 
 * // Usage
 * approvePasco('pasco-123');
 * ```
 */
export function useApprovePasco(
    options?: Omit<UseMutationOptions<PascoDTO, Error, string>, 'mutationFn'>
) {
    const pascoService = getPascoService();
    const queryClient = useQueryClient();

    // Extract user's onSuccess before creating mutation
    const { onSuccess: userOnSuccess, ...restOptions } = options || {};

    return useMutation({
        ...restOptions,
        mutationFn: async (pascoId: string) => {
            return await pascoService.approve(pascoId);
        },
        onSuccess: async (data, variables, context, extra) => {
            // Invalidate and refetch pascos list
            await queryClient.invalidateQueries({
                queryKey: ['pascos'],
                predicate: (query) => query.queryKey.length === 1 // Only invalidate list queries
            });

            // Update the specific pasco in cache
            queryClient.setQueryData(['pascos', variables], data);

            // Call user's onSuccess callback if provided
            if (userOnSuccess) {
                await userOnSuccess(data, variables, context, extra);
            }
        },
    });
}
